import type { WorkspaceConfig } from "@/shared";

import { core } from "@tauri-apps/api";
import { useState, useEffect } from "react";

const metaKeys = new Set(["Settings", "Meta", "System", "Version"]);

const workspaceOrder = ["Nexus", "Investments", "Trading", "ResponsumOS", "Real Estate", "TracQ"];

const filterWorkspaceConfig = (rawConfig: WorkspaceConfig): WorkspaceConfig => {
  // Dynamische Filterung: Alle Workspaces außer Meta-Keys
  const allWorkspaces = Object.keys(rawConfig).filter((key) => !metaKeys.has(key));

  // Sortiere nach workspaceOrder, dann alphabetisch
  const sortedWorkspaces = [...allWorkspaces];
  sortedWorkspaces.sort((a, b) => {
    const aIndex = workspaceOrder.indexOf(a);
    const bIndex = workspaceOrder.indexOf(b);

    // Wenn beide in workspaceOrder sind, sortiere nach Index
    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex;
    }

    // Wenn nur einer in workspaceOrder ist, priorisiere ihn
    if (aIndex !== -1) {
      return -1;
    }
    if (bIndex !== -1) {
      return 1;
    }

    // Ansonsten alphabetisch
    return a.localeCompare(b);
  });

  const filtered = Object.fromEntries(
    sortedWorkspaces.map((key) => [key, rawConfig[key]]),
  ) as WorkspaceConfig;
  // NOTE: Preserve Settings if it exists, even though it's filtered from workspaces
  if (rawConfig.Settings) {
    filtered.Settings = rawConfig.Settings;
  }
  return filtered;
};

/**
 * WHAT:
 *   Hook that provides workspace configuration with loading state.
 *
 * WHY:
 *   Enables components to access workspace/aspect configuration with proper
 *   loading state management.
 *
 * @returns {[WorkspaceConfig, (config: WorkspaceConfig) => void, boolean]} Workspace config, setter function, and loading state
 *
 */
export function useWorkspaceConfig(): [WorkspaceConfig, (cfg: WorkspaceConfig) => void, boolean] {
  const [config, setConfigState] = useState<WorkspaceConfig>({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        // Debug: Prüfe Settings-Tabelle separat
        // NOTE: Debug calls removed - were not using returned data
        // Debug: Prüfe Settings-Tabelle separat
        try {
          await core.invoke<unknown>("debug_settings_table");
        } catch {
          // NOTE: Debug call failed - silently ignore
        }

        // Debug: Prüfe Workspaces-Tabelle separat
        try {
          await core.invoke<unknown>("debug_workspaces_table");
        } catch {
          // NOTE: Debug call failed - silently ignore
        }

        // Debug: Prüfe kombinierte Datenbankinhalte
        try {
          await core.invoke<unknown>("debug_database_content");
        } catch {
          // NOTE: Debug call failed - silently ignore
        }

        const data = await core.invoke<WorkspaceConfig>("load_workspace_config");

        const filtered = filterWorkspaceConfig(data);
        setConfigState(filtered);
      } catch {
        // NOTE: Failed to load config - set empty config state
        setConfigState({});
      } finally {
        setIsLoading(false);
      }
    };

    void loadConfig();
  }, []);

  const saveConfig = async (cfg: WorkspaceConfig) => {
    setConfigState(cfg);
    try {
      await core.invoke("save_workspace_config", { config: cfg });
    } catch {
      // NOTE: Failed to save config - silently ignore to prevent UI disruption
    }
  };

  return [config, saveConfig, isLoading];
}
